import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Plus } from 'lucide-react'
import toast from 'react-hot-toast'

interface PlatformModule {
  id: string
  name: string
  name_he: string
  module_code: string
  description: string
  icon: string
  color: string
  is_active: boolean
  org_count?: number
}

const MODULE_COLORS: Record<string, string> = {
  crm: '#5de6ff',
  events: '#c0c1ff',
  calendar: '#4edea3',
  finance: '#fbbf24',
  digital: '#f97316',
  communication: '#e879f9',
  analytics: '#8083ff',
  settings: '#908fa0',
}

export default function ModulesPage() {
  const [modules, setModules] = useState<PlatformModule[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('platform_modules')
        .select('*, org_modules(id)')
        .order('name')
      const rows = (data ?? []).map((m: any) => ({
        ...m,
        org_count: Array.isArray(m.org_modules)
          ? m.org_modules.filter((om: any) => om.is_active !== false).length
          : 0,
      }))
      setModules(rows)
      setLoading(false)
    }
    load()
  }, [])

  const toggleModule = async (mod: PlatformModule) => {
    const { error } = await supabase
      .from('platform_modules')
      .update({ is_active: !mod.is_active })
      .eq('id', mod.id)
    if (!error) {
      setModules(prev => prev.map(m => m.id === mod.id ? { ...m, is_active: !m.is_active } : m))
      toast.success(`Module "${mod.name_he || mod.name}" ${!mod.is_active ? 'enabled' : 'disabled'}`)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: '#6366f1' }} />
    </div>
  )

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1
            className="text-2xl font-bold"
            style={{ color: '#ffffff', letterSpacing: '-0.02em' }}
          >
            Module Catalog
          </h1>
          <p className="text-sm mt-0.5" style={{ color: '#9ca3af', opacity: 0.6 }}>
            Platform modules available to organizations
          </p>
        </div>
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors w-full sm:w-auto justify-center sm:justify-start"
          style={{ background: '#6366f1' }}
          onMouseEnter={e => e.currentTarget.style.background = '#4f46e5'}
          onMouseLeave={e => e.currentTarget.style.background = '#6366f1'}
        >
          <Plus size={14} /> New Module
        </button>
      </div>

      {/* Module grid */}
      {modules.length === 0 ? (
        <div
          className="rounded-xl p-12 text-center text-sm"
          style={{ background: '#252836', color: '#9ca3af', opacity: 0.4 }}
        >
          No modules found
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {modules.map(mod => {
            const color = mod.color ?? MODULE_COLORS[mod.module_code] ?? '#6366f1'
            return (
              <div
                key={mod.id}
                className="rounded-xl overflow-hidden transition-all duration-150"
                style={{
                  background: '#252836',
                  borderTop: `3px solid ${color}`,
                  opacity: mod.is_active ? 1 : 0.55,
                }}
              >
                <div className="p-4">
                  {/* Icon + toggle row */}
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                      style={{ background: color + '1a' }}
                    >
                      {mod.icon?.startsWith('http') ? (
                        <img src={mod.icon} alt="" className="w-6 h-6" />
                      ) : (
                        <span>{mod.icon ?? '🔧'}</span>
                      )}
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => toggleModule(mod)}
                      className="relative flex-shrink-0 w-11 h-6 rounded-full transition-all duration-200"
                      style={{ background: mod.is_active ? '#4edea3' : '#2d3148' }}
                    >
                      <span
                        className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: mod.is_active ? 'translateX(20px)' : 'translateX(0)' }}
                      />
                    </button>
                  </div>

                  {/* Names */}
                  <div className="font-semibold text-sm mb-0.5" style={{ color: '#ffffff' }}>
                    {mod.name}
                  </div>
                  {mod.name_he && (
                    <div
                      className="text-xs mb-1"
                      dir="rtl"
                      style={{ color: '#9ca3af', opacity: 0.6 }}
                    >
                      {mod.name_he}
                    </div>
                  )}
                  <code
                    className="text-xs"
                    style={{
                      color: '#9ca3af',
                      opacity: 0.4,
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  >
                    {mod.module_code}
                  </code>

                  {/* Footer */}
                  <div
                    className="mt-3 pt-3 flex items-center justify-between"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span
                      className="text-xs"
                      style={{ color: '#9ca3af', opacity: 0.5 }}
                    >
                      Used by <span className="num">{mod.org_count ?? 0}</span> orgs
                    </span>
                    {mod.description && (
                      <span
                        className="text-xs truncate max-w-24"
                        style={{ color: '#9ca3af', opacity: 0.35 }}
                      >
                        {mod.description}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
